import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  ASSET_VISIBILITIES,
  ASSET_VISIBILITY_LABEL,
  type AssetVisibility,
  type PortalFaqItem,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useCreateFaqItem } from "./use-create-faq-item";
import { useUpdateFaqItem } from "./use-update-faq-item";

type Mode = { kind: "create" } | { kind: "edit"; item: PortalFaqItem };

export function FaqDialog({
  open,
  onOpenChange,
  mode,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  mode: Mode;
}) {
  const createMutation = useCreateFaqItem();
  const updateMutation = useUpdateFaqItem();

  const [category, setCategory] = useState(
    mode.kind === "edit" ? mode.item.category : "General",
  );
  const [question, setQuestion] = useState(
    mode.kind === "edit" ? mode.item.question : "",
  );
  const [answerMd, setAnswerMd] = useState(
    mode.kind === "edit" ? mode.item.answer_md : "",
  );
  const [position, setPosition] = useState(
    mode.kind === "edit" ? mode.item.position : 0,
  );
  const [visibility, setVisibility] = useState<AssetVisibility>(
    mode.kind === "edit" ? mode.item.visibility : "all_partners",
  );
  const [active, setActive] = useState(
    mode.kind === "edit" ? mode.item.active : true,
  );

  const pending = createMutation.isPending || updateMutation.isPending;

  function submit() {
    const payload = {
      category,
      question,
      answer_md: answerMd,
      position,
      visibility,
      active,
    };
    if (mode.kind === "create") {
      createMutation.mutate(payload, {
        onSuccess: () => {
          toast.success("FAQ added");
          onOpenChange(false);
        },
      });
    } else {
      updateMutation.mutate(
        { id: mode.item.id, input: payload },
        {
          onSuccess: () => {
            toast.success("FAQ updated");
            onOpenChange(false);
          },
        },
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {mode.kind === "create" ? "New FAQ entry" : "Edit FAQ"}
          </DialogTitle>
          <DialogDescription>
            Markdown is rendered as plain text with preserved line breaks in
            v1; richer formatting arrives later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="faq-category">Category</Label>
              <Input
                id="faq-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Getting started"
              />
            </div>
            <div>
              <Label htmlFor="faq-position">Position</Label>
              <Input
                id="faq-position"
                type="number"
                min={0}
                value={position}
                onChange={(e) =>
                  setPosition(Number.parseInt(e.target.value || "0", 10) || 0)
                }
              />
            </div>
          </div>

          <div>
            <Label htmlFor="faq-question">Question</Label>
            <Input
              id="faq-question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="How do I register a new deal?"
            />
          </div>

          <div>
            <Label htmlFor="faq-answer">Answer</Label>
            <Textarea
              id="faq-answer"
              rows={6}
              value={answerMd}
              onChange={(e) => setAnswerMd(e.target.value)}
              placeholder="Go to **Register a deal** from your portal home…"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Visibility</Label>
              <Select
                value={visibility}
                onValueChange={(v) => setVisibility(v as AssetVisibility)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASSET_VISIBILITIES.map((v) => (
                    <SelectItem key={v} value={v}>
                      {ASSET_VISIBILITY_LABEL[v]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <label className="flex w-full cursor-pointer items-center justify-between rounded-md border bg-muted/20 px-3 py-2 text-sm">
                <span>Active</span>
                <Switch checked={active} onCheckedChange={setActive} />
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending}>
            {pending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
            {mode.kind === "create" ? "Add FAQ" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
